import React, { useEffect } from "react"

import { ArticleContainer } from "../../styles/Container"

import { AppState } from "../../types"
import { useInterval } from "@nvdb-rapport/shared/src/hooks/useInterval"
import { alertBeforeUnload } from "@nvdb-rapport/shared/src/utils/alertBeforeUnload"
import { pollContract } from "../../data/api"
import { useImmer } from "use-immer"
import formatDistanceToNow from "date-fns/formatDistanceToNow"
import bokmaalLocale from "date-fns/locale/nb"

import { timeDistance } from "../../utils/duration"

import { ReportAction, ReportModel } from "../../update/report"
import { UIModel, UIAction } from "../../update/ui"
import Head from "@nvdb-rapport/shared/src/components/Head"

import { EitherAsync, Just } from "purify-ts"
import {
  SuccessPoll,
  PollData,
  PendingPoll,
  UnknownPoll,
  ErrorPoll,
} from "@nvdb-rapport/shared/src/ApiData"
import { GeneriskModel } from "./update"
import { LoadingReportSpinner } from "@nvdb-rapport/shared/src/components/LoadingReportSpinner"
import tw from "twin.macro"
import { LoadingReportView } from "../Driftskontrakt/FetchingForm"

interface FetchingFormQueryProps {
  reportState: ReportModel
  reportDispatch: React.Dispatch<ReportAction>
  uiState: UIModel
  uiDispatch: React.Dispatch<UIAction>
  generiskState: GeneriskModel
}

function getPollResult(pollId: string) {
  return EitherAsync<string, PollData>(async ({ liftEither }) => {
    const poll = await pollContract(pollId)
    const pollResult = await liftEither(poll)

    return pollResult
  })
}

export default function FetchingFormQuery({
  reportDispatch,
  reportState,
  uiDispatch,
  uiState,
  generiskState,
}: FetchingFormQueryProps): JSX.Element {
  useInterval(
    async () => {
      reportState.contract.caseOf({
        Nothing: async () => {
          console.log("Ingen kontrakt")
        },
        Just: async contract => {
          const pollResult = await getPollResult(contract.id).run()
          pollResult
            .ifRight(poll => {
              switch (poll.status) {
                case "SUCCESS":
                  const successPoll = poll as SuccessPoll
                  reportDispatch({
                    type: "SetReport",
                    payload: Just(successPoll),
                  })
                  uiDispatch({
                    type: "UpdateAppState",
                    payload: AppState.DATA_READY,
                  })
                  break
                case "PENDING":
                  const pendingPoll = poll as PendingPoll

                  reportDispatch({
                    type: "SetReport",
                    payload: Just(pendingPoll),
                  })

                  break
                case "UNKNOWN":
                  const unknowPoll = poll as UnknownPoll
                  reportDispatch({
                    type: "SetReport",
                    payload: Just(unknowPoll),
                  })
                  uiDispatch({
                    type: "AddError",
                    payload: {
                      message: "Unkown",
                      title: `UNKNOWN`,
                    },
                  })
                  uiDispatch({
                    type: "UpdateAppState",
                    payload: AppState.ERROR,
                  })
                  break
                case "ERROR":
                  const errorPoll = poll as ErrorPoll
                  reportDispatch({
                    type: "SetReport",
                    payload: Just(errorPoll),
                  })
                  uiDispatch({
                    type: "AddError",
                    payload: {
                      message: errorPoll.error,
                      title: `POLL: (${timeDistance(errorPoll.durationMs)})`,
                    },
                  })
                  break
              }
            })
            .ifLeft(e => {
              uiDispatch({
                type: "AddError",
                payload: {
                  message: e,
                  title: "Feil ved innhenting av data",
                },
              })
            })
        },
      })
    },
    uiState.appState === AppState.CONTRACT_PENDING ? 3000 : null
  )

  useEffect(() => {
    typeof window !== "undefined" &&
      window.addEventListener("beforeunload", alertBeforeUnload)
    return () => {
      typeof window !== "undefined" &&
        window.removeEventListener("beforeunload", alertBeforeUnload)
    }
  }, [uiState])

  const selectedArea = generiskState.selectedContractArea.extract()
  return (
    <>
      <Head
        title={selectedArea?.navn}
        siteTitle="Vegnett og objektdata for driftskontrakter"
        status="yellow"
      ></Head>
      {reportState.contract.caseOf({
        Nothing: () => (
          <ArticleContainer style={tw`mx-auto`}>
            <LoadingReportSpinner />
          </ArticleContainer>
        ),
        Just: contract => {
          const duration =
            contract.status === "PENDING"
              ? contract.durationMsString
              : "00:00:00"
          return contract.status === "PENDING" ? (
            <ArticleContainer style={tw`mx-auto`}>
              <LoadingReportSpinner durationMsString={duration} />
            </ArticleContainer>
          ) : (
            <LoadingReportView durationMsString={duration} />
          )
        },
      })}
    </>
  )
}
